import { useEffect, useState } from 'react';
import { useStateLocalStorage } from '../hooks/useStateLocalStorage';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, Modal } from '@convergence/components';
import { FileBrowserSelectionType, ValueType } from '@convergence/types';
import { AnalysisInputProps } from '../components/analysis-value/input/analysis-inputs';
import {
  LabFileBrowser,
  useLabFileBrowser
} from '../components/lab-file-browser';
import { Folder } from '@phosphor-icons/react';
import { Help } from '../components/Help';
import { AnalysisAction } from '../components/analysis-value/action/analysis-actions';
import { useCreateActionMutation } from '../api/mutations/createAction';
import { AnalysisSchemaDtoType } from '@convergence/client';
import { AnalysisSchemaSelector } from '../components/AnalysisSchemaSelector';
import { useAnalysisSchemaQuery } from '../api/queries/analysisSchema';

export function CreateAction() {
  const [selectedSchemaAndVersion, setSelectedSchemaAndVersion] = useState<{
    schema: AnalysisSchemaDtoType;
    version: number;
  } | null>(null);
  const analysisSchemaQuery = useAnalysisSchemaQuery(
    selectedSchemaAndVersion?.schema.id,
    selectedSchemaAndVersion?.version
  );

  const randomStarWarsCharacterQuery = useQuery({
    queryKey: ['randomStarWarsCharacter'],
    queryFn: async () => {
      const response = await fetch(
        'https://random-star-wars-character.vercel.app/api',
        { method: 'GET' }
      );
      const data = await response.text();
      return data;
    }
  });

  const [actionName, setActionName] = useStateLocalStorage('actionName', {
    touched: false,
    value: randomStarWarsCharacterQuery.data || ''
  });

  useEffect(() => {
    if (randomStarWarsCharacterQuery.data && !actionName.touched) {
      setActionName({
        touched: false,
        value: randomStarWarsCharacterQuery.data
      });
    }
  }, [randomStarWarsCharacterQuery.data]);

  const [showTriggerLocationModal, setShowTriggerLocationModal] =
    useState(false);
  const [triggerLocation, setTriggerLocation] = useStateLocalStorage<{
    bucketId?: number;
    path: string;
    isFolder: boolean;
    labName: string;
  } | null>('triggerLocation', null);

  const createActionMutation = useCreateActionMutation();

  const { path, setPath, bucketId, setBucketId } = useLabFileBrowser();

  console.log({ selectedSchemaAndVersion });
  const selectedSchemaVersion = analysisSchemaQuery.data?.versions.find(
    (v) => v.version === selectedSchemaAndVersion?.version
  );
  console.log('Selected Schema Version:', selectedSchemaVersion);

  return (
    <div className="h-full overflow-scroll p-8 bg-white">
      <div className="grid grid-rows-3 grid-cols-1 mb-4 justify-center items-start ">
        <div className="flex gap-4 border-b border-black items-center w-full h-full border-l border-t p-4 border-r">
          <label className="">Analysis Type:</label>
          <AnalysisSchemaSelector
            onChange={(schema, version) => {
              setSelectedSchemaAndVersion({ schema, version });
            }}
          />
        </div>
        <div className="flex gap-4 border-b border-black items-center h-full border-l p-4 border-r">
          <label>Name:</label>
          <Input
            onChange={(e) =>
              setActionName({ touched: true, value: e.target.value })
            }
            value={actionName.value}
          />
        </div>
        <div className="flex gap-4 items-center h-full border-b border-black border-l p-4 border-r whitespace-nowrap overflow-hidden">
          <label className="flex items-center gap-1 ">
            Trigger Location:
            <Help>
              <p>Trigger the action when new files are uploaded to this path</p>
            </Help>
          </label>
          <Button
            onClick={() => setShowTriggerLocationModal(true)}
            className="flex gap-2 items-center "
          >
            <Folder className="inline" />{' '}
            {triggerLocation
              ? `${triggerLocation.labName}://${triggerLocation.path}/`
              : 'Select Trigger Location'}
          </Button>
          <Modal
            visible={showTriggerLocationModal}
            onClose={() => setShowTriggerLocationModal(false)}
            className=""
          >
            <div className="p-4 h-full overflow-hidden flex flex-col">
              <LabFileBrowser
                selection={triggerLocation ? [triggerLocation] : []}
                pathType={FileBrowserSelectionType.DIR}
                onSelectionChange={(selection, lab) => {
                  if (selection.length > 0) {
                    setTriggerLocation({
                      ...selection[0]!,
                      labName: lab.name
                    });
                  }
                }}
                bucketId={bucketId}
                setBucketId={(bucketId: number) => {
                  setBucketId(bucketId);
                }}
                path={path}
                setPath={setPath}
              />
              <Button
                className="mt-4"
                onClick={() => setShowTriggerLocationModal(false)}
              >
                Select
              </Button>
            </div>
          </Modal>
        </div>
      </div>

      <div className="border-black border p-4">
        {selectedSchemaVersion && (
          <AnalysisAction
            inputs={
              selectedSchemaVersion.inputs as AnalysisInputProps['inputs']
            }
            onSubmit={async (input) => {
              if (!triggerLocation || !selectedSchemaAndVersion) {
                return;
              }
              await createActionMutation.mutateAsync({
                name: actionName.value,
                analysisSchemaId: selectedSchemaAndVersion.schema.id,
                analysisSchemaVersion: selectedSchemaAndVersion.version,
                triggerLocation: {
                  bucketId: triggerLocation.bucketId!,
                  folderPath: triggerLocation.path
                },
                config: Object.entries(input).reduce(
                  (acc, [key, value]) => {
                    // remove errors and get schema
                    acc[+key] = value.schema;
                    return acc;
                  },
                  {} as Record<number, ValueType>
                )
              });

              // const res = await createAndLaunchAnalysisMutation.mutateAsync({
              //   input: Object.entries(input).reduce(
              //     (acc, [key, value]) => {
              //       // remove errors and get schema
              //       acc[+key] = value.schema;
              //       return acc;
              //     },
              //     {} as Record<number, ValueType>
              //   ),
              //   outputLocation: {
              //     s3BucketId: triggerLocation!.bucketId!, // TODO: check for null
              //     path: triggerLocation!.path
              //   },
              //
              //   userId: user!.id,
              //   analysisSchemaId: selectedSchema.id,
              //   name: analysisName.value
              // });
              // if (res.analysisId) {
              //   navigate(`/a/${res.analysisSlug}`);
              // }
            }}
          />
        )}
      </div>
    </div>
  );
}
